import * as THREE from 'three/webgpu';

/**
 * 草图旋转实体鼠标角度拖动会话。
 *
 * 设计说明：
 * 1. 本类只把旋转中心附近的鼠标径向向量转换为连续角度，不读取 Viewer 或修改草图实体；
 * 2. 拖动开始时保存 PropertyManager 的当前绝对角度，后续只累计鼠标相邻帧之间的最短有符号角；
 * 3. 相邻帧跨越 `±180°` 时会展开到连续分支，避免实体和辅助轴突然反向跳转；
 * 4. 鼠标暂时靠近旋转中心时保留上一有效状态，不用无定义方向破坏当前预览。
 */
export class SketchRotatePointerDragSession {
	/** 当前是否存在有效拖动会话。 */
	private active = false;

	/** 拖动开始时已有的绝对角度。 */
	private startAngleDeg = 0;

	/** 拖动期间已经累计的连续弧度增量。 */
	private accumulatedDeltaRad = 0;

	/** 上一有效鼠标径向角。 */
	private lastPointerAngleRad = 0;

	/** 拖动旋转中心世界坐标。 */
	private readonly centerWorld = new THREE.Vector3();

	/** 草图参考面的 X 轴世界方向。 */
	private readonly xAxisWorld = new THREE.Vector3(1, 0, 0);

	/** 草图参考面的 Y 轴世界方向。 */
	private readonly yAxisWorld = new THREE.Vector3(0, 1, 0);

	/** 鼠标径向方向复用向量。 */
	private readonly pointerDirection = new THREE.Vector3();

	/**
	 * 开始鼠标角度拖动。
	 *
	 * @param startAngleDeg 拖动开始时 PropertyManager 的绝对角度。
	 * @param centerWorld 旋转中心世界坐标。
	 * @param xAxisWorld 草图参考面 X 轴世界方向。
	 * @param yAxisWorld 草图参考面 Y 轴世界方向。
	 * @param pointerWorld 鼠标与草图平面的世界交点。
	 * @returns 输入坐标系与鼠标径向方向有效时返回 true。
	 */
	public start(
		startAngleDeg: number,
		centerWorld: THREE.Vector3,
		xAxisWorld: THREE.Vector3,
		yAxisWorld: THREE.Vector3,
		pointerWorld: THREE.Vector3,
	): boolean {
		this.end();
		if (
			!Number.isFinite(startAngleDeg)
			|| xAxisWorld.lengthSq() <= 1e-12
			|| yAxisWorld.lengthSq() <= 1e-12
		) {
			return false;
		}

		this.centerWorld.copy(centerWorld);
		this.xAxisWorld.copy(xAxisWorld).normalize();
		this.yAxisWorld.copy(yAxisWorld).normalize();
		const pointerAngleRad = this.resolvePointerAngleRad(pointerWorld);
		if (pointerAngleRad === null) {
			return false;
		}

		this.startAngleDeg = startAngleDeg;
		this.accumulatedDeltaRad = 0;
		this.lastPointerAngleRad = pointerAngleRad;
		this.active = true;
		return true;
	}

	/**
	 * 按当前鼠标位置更新连续绝对角度。
	 *
	 * @param pointerWorld 鼠标与草图平面的世界交点。
	 * @returns 当前绝对角度；会话未激活或鼠标方向无效时返回 null。
	 */
	public update(pointerWorld: THREE.Vector3): number | null {
		if (!this.active) {
			return null;
		}

		const pointerAngleRad = this.resolvePointerAngleRad(pointerWorld);
		if (pointerAngleRad === null) {
			return null;
		}

		// 将相邻帧差值收口到最短有符号角，跨过 -π/π 时继续沿原拖动方向累计。
		let frameDeltaRad = pointerAngleRad - this.lastPointerAngleRad;
		if (frameDeltaRad > Math.PI) {
			frameDeltaRad -= Math.PI * 2;
		} else if (frameDeltaRad < -Math.PI) {
			frameDeltaRad += Math.PI * 2;
		}

		this.accumulatedDeltaRad += frameDeltaRad;
		this.lastPointerAngleRad = pointerAngleRad;
		return this.startAngleDeg + THREE.MathUtils.radToDeg(this.accumulatedDeltaRad);
	}

	/**
	 * 结束当前拖动会话并保留业务层已经应用的最后角度。
	 */
	public end(): void {
		this.active = false;
		this.accumulatedDeltaRad = 0;
	}

	/**
	 * 判断当前是否处于有效拖动态。
	 *
	 * @returns 拖动会话有效时返回 true。
	 */
	public isActive(): boolean {
		return this.active;
	}

	/**
	 * 解析鼠标径向向量在当前草图参考坐标系中的有符号角。
	 *
	 * @param pointerWorld 鼠标与草图平面的世界交点。
	 * @returns `[-π, π]` 范围内的径向角；鼠标过于接近中心时返回 null。
	 */
	private resolvePointerAngleRad(pointerWorld: THREE.Vector3): number | null {
		this.pointerDirection.copy(pointerWorld).sub(this.centerWorld);
		if (this.pointerDirection.lengthSq() <= 1e-12) {
			return null;
		}

		return Math.atan2(
			this.pointerDirection.dot(this.yAxisWorld),
			this.pointerDirection.dot(this.xAxisWorld),
		);
	}
}
